import { archiveReasonLabels } from "@albertine/types";
import { format, isValid, parseISO } from "date-fns";
import { Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ArchivedMember, ProfileGroup } from "../data/profile";
import type { OpenProfileOverlay } from "../overlays/profileOverlay";
import { ProfileParagraph, ProfileSection } from "../profilePrimitives";

export function ArchivedBanner({
    archived,
    canActivate,
    onActivate,
}: {
    archived: ArchivedMember;
    canActivate: boolean;
    onActivate: () => void;
}) {
    return (
        <div className="flex flex-col gap-3 px-3 pt-5">
            <ProfileParagraph>
                This member has been archived {formatArchivedDate(archived.date)} due to{" "}
                {archiveReasonLabel(archived.reason).toLocaleLowerCase()}.
            </ProfileParagraph>
            {canActivate && <Button onClick={onActivate}>Activate member</Button>}
        </div>
    );
}

export function ArchiveSection({
    groups,
    setOverlay,
}: {
    groups: ProfileGroup[];
    setOverlay: OpenProfileOverlay;
}) {
    const hasGroups = groups.length > 0;

    return (
        <ProfileSection title="Archive member" className="pb-10">
            <ProfileParagraph muted>
                By archiving a member you make their user account inactive and they are
                not shown in the member list.
            </ProfileParagraph>
            {hasGroups && (
                <ProfileParagraph muted>
                    You must first remove member from all groups.
                </ProfileParagraph>
            )}
            <Button
                variant="destructive"
                onClick={() => setOverlay({ type: "archive" })}
            >
                <Trash2 data-icon="inline-start" />
                Archive member
            </Button>
        </ProfileSection>
    );
}

function formatArchivedDate(value: string) {
    const parsed = parseISO(value);
    return isValid(parsed) ? format(parsed, "dd MMM yyyy") : value;
}

function archiveReasonLabel(reason: string) {
    return archiveReasonLabels.find(([id]) => id === reason)?.[1] ?? reason;
}
